import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, X } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { CATEGORIES, PRIORITIES, REPEATS } from "@/lib/translations";
import { localToIso, isoToLocalInput } from "@/lib/datetime";

export const EditTaskDialog = ({ open, onOpenChange, task, onSave }) => {
  const { t } = useApp();
  const [title, setTitle] = useState(task.title);
  const [category, setCategory] = useState(task.category || "none");
  const [priority, setPriority] = useState(task.priority || "medium");
  const [repeat, setRepeat] = useState(task.repeat || "none");
  const [dueDate, setDueDate] = useState(isoToLocalInput(task.due_date));
  const [reminder, setReminder] = useState(isoToLocalInput(task.reminder_at));
  const [notes, setNotes] = useState(task.notes || "");
  const [checklist, setChecklist] = useState(task.checklist || []);
  const [newItem, setNewItem] = useState("");

  useEffect(() => {
    if (open) {
      setTitle(task.title);
      setCategory(task.category || "none");
      setPriority(task.priority || "medium");
      setRepeat(task.repeat || "none");
      setDueDate(isoToLocalInput(task.due_date));
      setReminder(isoToLocalInput(task.reminder_at));
      setNotes(task.notes || "");
      setChecklist(task.checklist || []);
      setNewItem("");
    }
  }, [open, task]);

  const addItem = () => {
    const text = newItem.trim();
    if (!text) return;
    setChecklist((prev) => [...prev, { id: crypto.randomUUID(), text, done: false }]);
    setNewItem("");
  };
  const toggleItem = (id) =>
    setChecklist((prev) => prev.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  const removeItem = (id) => setChecklist((prev) => prev.filter((i) => i.id !== id));

  const save = async () => {
    const value = title.trim();
    if (!value) return;
    await onSave(task.id, {
      title: value,
      category: category === "none" ? null : category,
      priority,
      repeat,
      due_date: localToIso(dueDate),
      reminder_at: localToIso(reminder),
      notes: notes.trim() || null,
      checklist,
    });
    onOpenChange(false);
  };

  const trigger = "h-11 rounded-xl bg-background";
  const field = "w-full h-11 bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="edit-task-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("editTitle")}</DialogTitle>
          <DialogDescription className="sr-only">{t("editTitle")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label>{t("addButton")}</Label>
            <input
              data-testid="edit-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && save()}
              className="w-full h-11 bg-background border border-border rounded-xl px-3 outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("category")}</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid="edit-category-select" className={trigger}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t("noCategory")}</SelectItem>
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c.color }} />
                        {t(c.labelKey)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("priority")}</Label>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger data-testid="edit-priority-select" className={trigger}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      <span className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: p.color }} />
                        {t(p.labelKey)}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>{t("repeat")}</Label>
              <Select value={repeat} onValueChange={setRepeat}>
                <SelectTrigger data-testid="edit-repeat-select" className={trigger}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REPEATS.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {t(r.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("dueDate")}</Label>
              <input
                type="datetime-local"
                data-testid="edit-due-date-input"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className={field}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t("reminder")}</Label>
            <input
              type="datetime-local"
              data-testid="edit-reminder-input"
              value={reminder}
              onChange={(e) => setReminder(e.target.value)}
              className={field}
            />
          </div>

          <div className="space-y-2">
            <Label>{t("notes")}</Label>
            <Textarea
              data-testid="edit-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("notesPlaceholder")}
              rows={3}
              className="rounded-xl bg-background resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label>{t("checklist")}</Label>
            <div className="space-y-1.5" data-testid="edit-checklist">
              {checklist.map((item) => (
                <div key={item.id} className="flex items-center gap-2 group/item">
                  <button
                    type="button"
                    data-testid={`edit-checklist-toggle-${item.id}`}
                    onClick={() => toggleItem(item.id)}
                    className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors ${
                      item.done ? "bg-success border-success text-white" : "border-muted-foreground/40"
                    }`}
                  >
                    {item.done && <span className="text-[10px] font-bold">✓</span>}
                  </button>
                  <span className={`flex-1 text-sm ${item.done ? "line-through text-muted-foreground" : ""}`}>
                    {item.text}
                  </span>
                  <button
                    type="button"
                    data-testid={`edit-checklist-remove-${item.id}`}
                    onClick={() => removeItem(item.id)}
                    className="w-6 h-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <input
                data-testid="edit-checklist-input"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addItem();
                  }
                }}
                placeholder={t("addItem")}
                className="flex-1 h-10 bg-background border border-border rounded-xl px-3 text-sm outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary transition-all"
              />
              <button
                type="button"
                data-testid="edit-checklist-add"
                onClick={addItem}
                className="h-10 w-10 shrink-0 rounded-xl bg-secondary text-foreground flex items-center justify-center hover:bg-accent transition-colors"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            data-testid="edit-cancel-btn"
            onClick={() => onOpenChange(false)}
            className="h-10 px-4 rounded-xl text-sm font-medium text-muted-foreground hover:bg-secondary transition-colors"
          >
            {t("cancel")}
          </button>
          <button
            data-testid="edit-save-btn"
            onClick={save}
            className="h-10 px-5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110 active:scale-[0.98] transition-all"
          >
            {t("save")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
